import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
  LuExternalLink,
  LuCalendar,
  LuTrendingUp,
  LuBell,
  LuArrowRight,
  LuSparkles,
  LuMessageSquare,
  LuFileText,
  LuClipboardCheck,
  LuCreditCard,
  LuFolderOpen,
  LuCalendarDays,
  LuChevronLeft,
  LuChevronRight,
  LuCircleHelp,
  LuClock,
  LuBadgeCheck,
  LuBookOpen
} from 'react-icons/lu';
import { useTheme, useSocket } from '../../context/SocketContext';
import { useRealtimeRefresh } from '../../context/RealtimeSyncContext';
import GuidancePopup from '../../components/GuidancePopup';
import PageHeader from '../../components/PageHeader';
import {
  PortalSectionIcon,
  SocialLinkCard,
  usePortalContent,
  buildResourceFileUrl,
  getPortalColor,
  getPortalIcon,
  normalizeSocialLinks,
} from '../../portal';
import { CardGridSkeleton } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import { FaEye, FaDownload } from 'react-icons/fa';

// Import Documents
import doc1 from '../../Assets/Quick Guide on USA Business Entities.pdf';
import doc2 from '../../Assets/important update for all business owners and professionals in our community..jpeg';
import doc3 from '../../Assets/Monthly Bookkeping Checklist for Startups.pdf';
import doc4 from '../../Assets/Common Bookkeping Misktakes Founders make.jpeg';
import doc5 from '../../Assets/US-India-Tax-Guide-for-NRIs-H1B-Professionals-and-US-Citizens (2).pdf';

// Relative time helper
const timeAgo = (date) => {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

// Notification type config
const getNotifConfig = (type) => {
  switch (type) {
    case "chat":
      return { icon: LuMessageSquare, borderColor: 'border-blue-200 dark:border-blue-800/50', bgColor: 'bg-blue-50 dark:bg-blue-900/20', iconBg: 'bg-blue-100 dark:bg-blue-900/50', iconColor: 'text-blue-600 dark:text-blue-400' };
    case "invoice":
      return { icon: LuFileText, borderColor: 'border-emerald-200 dark:border-emerald-800/50', bgColor: 'bg-emerald-50 dark:bg-emerald-900/20', iconBg: 'bg-emerald-100 dark:bg-emerald-900/50', iconColor: 'text-emerald-600 dark:text-emerald-400' };
    case "meeting":
      return { icon: LuCalendarDays, borderColor: 'border-purple-200 dark:border-purple-800/50', bgColor: 'bg-purple-50 dark:bg-purple-900/20', iconBg: 'bg-purple-100 dark:bg-purple-900/50', iconColor: 'text-purple-600 dark:text-purple-400' };
    case "checklist":
      return { icon: LuClipboardCheck, borderColor: 'border-amber-200 dark:border-amber-800/50', bgColor: 'bg-amber-50 dark:bg-amber-900/20', iconBg: 'bg-amber-100 dark:bg-amber-900/50', iconColor: 'text-amber-600 dark:text-amber-400' };
    case "payment":
    case "service_purchase":
      return { icon: LuCreditCard, borderColor: 'border-pink-200 dark:border-pink-800/50', bgColor: 'bg-pink-50 dark:bg-pink-900/20', iconBg: 'bg-pink-100 dark:bg-pink-900/50', iconColor: 'text-pink-600 dark:text-pink-400' };
    case "document":
      return { icon: LuFolderOpen, borderColor: 'border-cyan-200 dark:border-cyan-800/50', bgColor: 'bg-cyan-50 dark:bg-cyan-900/20', iconBg: 'bg-cyan-100 dark:bg-cyan-900/50', iconColor: 'text-cyan-600 dark:text-cyan-400' };
    default:
      return { icon: LuBell, borderColor: 'border-gray-200 dark:border-dark-600', bgColor: 'bg-gray-50 dark:bg-dark-700', iconBg: 'bg-gray-200 dark:bg-dark-600', iconColor: 'text-gray-600 dark:text-gray-400' };
  }
};

const Dashboard = () => {
  const { currentThemeColors } = useTheme();
  const socket = useSocket();
  const navigate = useNavigate();
  const { setChatboxOpen } = useOutletContext();
  const [blogs, setBlogs] = useState([]);
  const [showGuidance, setShowGuidance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blogPage, setBlogPage] = useState(1);
  const [totalBlogPages, setTotalBlogPages] = useState(1);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [upcomingMeetingsCount, setUpcomingMeetingsCount] = useState(0);
  const { content: dashboardContent } = usePortalContent('dashboard');

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch recent blogs
  const fetchBlogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/blogs?page=${page}&limit=4`
      );
      if (response.ok) {
        const data = await response.json();
        setBlogs(data.blogs || []);
        setTotalBlogPages(data.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs(blogPage);
  }, [blogPage, fetchBlogs]);

  useRealtimeRefresh('blogs', () => fetchBlogs(blogPage));

  const fetchUserProfile = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  }, []);

  const fetchMeetings = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/meetings/user/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const now = new Date();
        const soon = data.filter(m => new Date(m.startTime || m.start_time) > now);
        setUpcomingMeetingsCount(soon.length);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
    fetchMeetings();
  }, [fetchUserProfile, fetchMeetings]);

  useRealtimeRefresh('profile', fetchUserProfile);
  useRealtimeRefresh('meetings', fetchMeetings);

  // Show guidance popup only once per session
  useEffect(() => {
    const hasSeen = sessionStorage.getItem('hasSeenGuidance');
    if (!hasSeen) {
      const timer = setTimeout(() => {
        setShowGuidance(true);
        sessionStorage.setItem('hasSeenGuidance', 'true');
      }, 1500); // Small delay for better UX
      return () => clearTimeout(timer);
    }
  }, []);

  const scrollToWhatsNew = () => {
    const element = document.getElementById('whats-new-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Fetch notifications from new API
  const fetchNotifications = useCallback(async () => {
    if (!user?._id) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/notifications/user/${user._id}`
      );
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setNotifLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 15000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Socket.io real-time notifications
  useEffect(() => {
    if (!socket || !user?._id) return;

    socket.emit('joinRoom', user._id);

    const handleNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev]);
    };

    socket.on('newNotification', handleNewNotification);

    return () => {
      socket.off('newNotification', handleNewNotification);
    };
  }, [socket, user]);

  // Handle notification click on dashboard
  const handleNotifClick = async (notif) => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-read/${notif._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      setNotifications(prev => prev.filter(n => n._id !== notif._id));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }

    if (notif.link === '/chat') {
      setChatboxOpen(true);
    } else if (notif.link) {
      navigate(notif.link);
    }
  };

  // Truncate text helper
  const truncateText = (html, maxLength) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const userDisplayName = (() => {
    if (!user) return 'there';
    if (user.firstName) {
      return user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName;
    }
    if (user.fullName) return user.fullName;
    if (user.name) return user.name;
    if (user.email) return user.email.split('@')[0];
    return 'there';
  })();

  const fallbackResources = [
    { title: 'US-India Tax Guide for NRIs, H1B Professionals & US Citizens', description: 'A comprehensive guide on tax liabilities, double taxation, and filing requirements for NRIs and US expats.', icon: 'LuBookOpen', color: 'emerald', linkType: 'file', fileUrl: doc5, downloadName: 'US-India-Tax-Guide-for-NRIs-H1B-Professionals-and-US-Citizens.pdf', isActive: true },
    { title: 'Monthly Bookkeeping Checklist for Startups', description: 'Ensure nothing falls through the cracks with this essential monthly checklist for founders.', icon: 'LuClipboardCheck', color: 'blue', linkType: 'file', fileUrl: doc3, downloadName: 'Monthly_Bookkeeping_Checklist_for_Startups.pdf', isActive: true },
    { title: 'Common Bookkeeping Mistakes Founders Make', description: 'Learn how to avoid the most common financial pitfalls and keep your books clean.', icon: 'LuFolderOpen', color: 'purple', linkType: 'file', fileUrl: doc4, downloadName: 'Common_Bookkeeping_Mistakes.jpeg', isActive: true },
    { title: 'Quick Guide on USA Business Entities', description: 'A comprehensive guide to help you choose the right legal structure for your US business.', icon: 'LuFileText', color: 'primary', linkType: 'file', fileUrl: doc1, downloadName: 'Quick_Guide_USA_Business_Entities.pdf', isActive: true },
    { title: 'Mid-Year Tax Check: Are You Overpaying US Taxes?', description: 'Stay ahead of your taxes and identify potential savings with our mid-year evaluation.', icon: 'LuSparkles', color: 'secondary', linkType: 'file', fileUrl: doc2, downloadName: 'Community_Update.jpeg', isActive: true },
    { title: 'Detailed Help Guide', description: 'Need assistance? Explore our comprehensive help guide for step-by-step instructions.', icon: 'LuCircleHelp', color: 'amber', linkType: 'internal', linkUrl: '/help', isActive: true },
  ];

  const bundledFallbacks = [doc5, doc3, doc4, doc1, doc2];
  const portalResources = (dashboardContent?.resources || [])
    .filter((resource) => resource.isActive !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const displayResources = portalResources.length > 0 ? portalResources : fallbackResources;

  const socialLinks = normalizeSocialLinks(dashboardContent);

  const renderResourceActions = (resource, colors, fileHref) => {
    if (resource.linkType === 'internal') {
      return (
        <Link
          to={resource.linkUrl || '/help'}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-bold text-sm shadow-lg transition-all active:scale-95 mt-auto ${colors.actionBtn}`}
        >
          {resource.buttonText || 'Get Help'} <LuArrowRight className="w-4 h-4" />
        </Link>
      );
    }

    if (resource.linkType === 'external') {
      return (
        <a
          href={resource.linkUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-bold text-sm shadow-lg transition-all active:scale-95 mt-auto ${colors.actionBtn}`}
        >
          {resource.buttonText || 'Open'} <LuExternalLink className="w-4 h-4" />
        </a>
      );
    }

    return (
      <div className="flex items-center gap-3 mt-auto">
        <a href={fileHref} target="_blank" rel="noopener noreferrer" className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm transition-colors ${colors.viewBtn}`}>
          <FaEye className="w-4 h-4" /> View
        </a>
        <a href={fileHref} download={resource.downloadName || undefined} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-bold text-sm shadow-lg transition-all active:scale-95 ${colors.downloadBtn}`}>
          <FaDownload className="w-3 h-3" /> Download
        </a>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <GuidancePopup
        isOpen={showGuidance}
        onClose={() => setShowGuidance(false)}
        onOpenWhatNew={scrollToWhatsNew}
      />
      <PageHeader
        icon={getPortalIcon(dashboardContent?.welcomeIcon || 'LuSparkles')}
        title={<>{dashboardContent?.welcomeTitle || 'Welcome back'}, <span style={{ color: 'var(--primary-500)' }}>{userDisplayName}</span>!</>}
        subtitle={dashboardContent?.welcomeSubtitle || 'Stay updated with the latest news, blog posts, and notifications.'}
        action={
          <span className="px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-medium whitespace-nowrap">
            <LuCalendar className="inline-block w-4 h-4 mr-2" />
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </span>
        }
      />

      {/* Service Deadlines Card - Full Width between Banner and Content */}
      {user && (user.dueDate || user.annualReportService?.dueDate || user.registeredAgentService?.dueDate || (user.members && user.members.some(m => m.dueDate))) && (
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Tax Filing Deadlines */}
            {(user.dueDate || (user.members && user.members.some(m => m.dueDate))) && (
              <div className="p-4 rounded-2xl bg-white dark:bg-dark-800 border-none shadow-sm flex flex-col animated-gradient-border min-h-[160px]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-orange-600 dark:text-orange-400 uppercase">Tax Filing</h3>
                  <span className="text-xs font-bold text-orange-600 dark:text-orange-400 uppercase">Due Date</span>
                </div>
                <div className="space-y-4 flex-1">
                  {user.dueDate && (
                    <div className="flex flex-col group">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{user.name}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900 dark:text-white">{formatDate(user.dueDate)}</div>
                          {(() => {
                            const days = Math.ceil((new Date(user.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                            return days > 0 && <span className={`text-xs font-medium ${days <= 30 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>{days}d left</span>;
                          })()}
                        </div>
                      </div>
                      {user.taxFilingLocked ? (
                        <button 
                          onClick={(e) => { e.stopPropagation(); navigate('/pricing?service=service_2&step=1.5'); }}
                          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 transition-all active:scale-95 shadow-lg shadow-orange-600/20"
                        >
                          <LuCreditCard className="w-3.5 h-3.5" />
                          Pay Now
                        </button>
                      ) : (
                        <span className="px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center justify-center gap-1.5 w-full border border-emerald-100 dark:border-emerald-800/30">
                          <LuBadgeCheck className="w-3.5 h-3.5" />
                          Activated
                        </span>
                      )}
                    </div>
                  )}
                  {user.members && user.members.filter(m => m.dueDate).map((member, idx) => (
                    <div key={idx} className="flex flex-col pt-5 border-t border-gray-50 dark:border-dark-700 group">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate max-w-[150px]">{member.name}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900 dark:text-white">{formatDate(member.dueDate)}</div>
                          {(() => {
                            const days = Math.ceil((new Date(member.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                            return days > 0 && <span className={`text-xs font-medium ${days <= 30 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>{days}d left</span>;
                          })()}
                        </div>
                      </div>
                      {member.taxFilingLocked ? (
                        <button 
                          onClick={(e) => { e.stopPropagation(); navigate('/pricing?service=service_2&step=1.5'); }}
                          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 transition-all active:scale-95 shadow-lg shadow-orange-600/20"
                        >
                          <LuCreditCard className="w-3.5 h-3.5" />
                          Pay Now
                        </button>
                      ) : (
                        <span className="px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center justify-center gap-1.5 w-full border border-emerald-100 dark:border-emerald-800/30">
                          <LuBadgeCheck className="w-3.5 h-3.5" />
                          Activated
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {user.annualReportService?.dueDate && (
              <div className="p-4 rounded-2xl bg-white dark:bg-dark-800 border-none shadow-sm flex flex-col animated-gradient-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase">Annual Report</h3>
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase">Due Date</span>
                </div>
                <div className="flex items-center justify-between flex-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[150px]">{user.annualReportService.state} {user.annualReportService.entityType}</span>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {user.annualReportService.dueDateText || formatDate(user.annualReportService.dueDate)}
                    </div>
                    {user.annualReportService.dueDate && (() => {
                      const days = Math.ceil((new Date(user.annualReportService.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                      return days > 0 && <span className={`text-xs font-medium ${days <= 30 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>{days}d left</span>;
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-all active:scale-95 shadow-lg shadow-emerald-600/20"
                >
                  <LuCreditCard className="w-3.5 h-3.5" />
                  Pay Now
                </button>
              </div>
            )}

            {/* Registered Agent Deadline */}
            {user.registeredAgentService?.dueDate && (
              <div className="p-4 rounded-2xl bg-white dark:bg-dark-800 border-none shadow-sm flex flex-col animated-gradient-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase">Registered Agent</h3>
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase">Due Date</span>
                </div>
                <div className="flex items-center justify-between flex-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Service Renewal</span>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900 dark:text-white">{formatDate(user.registeredAgentService.dueDate)}</div>
                    {(() => {
                      const days = Math.ceil((new Date(user.registeredAgentService.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                      return days > 0 && <span className={`text-xs font-medium ${days <= 30 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>{days}d left</span>;
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-600/20"
                >
                  <LuCreditCard className="w-3.5 h-3.5" />
                  Pay Now
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Blog Posts - Takes 2 columns */}
        <div
          className="lg:col-span-2 card p-6 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <PortalSectionIcon iconName={dashboardContent?.blogSectionIcon || 'LuNewspaper'} />
              <h2 className="section-title">{dashboardContent?.blogSectionTitle || 'Latest Blog Posts'}</h2>
            </div>
            <a
              href={`${process.env.REACT_APP_MAIN_WEBSITE_URL || 'https://bookscomply.com'}/blog`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              {dashboardContent?.blogViewAllText || 'View All'}
              <LuArrowRight className="w-4 h-4" />
            </a>
          </div>

          {loading ? (
            <CardGridSkeleton count={4} columns="grid-cols-1 md:grid-cols-2" />
          ) : blogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {blogs.map((blog, index) => (
                <a
                  key={blog._id}
                  href={`${process.env.REACT_APP_MAIN_WEBSITE_URL || 'https://bookscomply.com'}/blog/${blog.slug || blog._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl overflow-hidden border border-gray-100 dark:border-dark-600 hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  {blog.image && (
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/${blog.image}`}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      {blog.category && (
                        <span className="absolute bottom-2 left-2 px-2 py-1 rounded-full bg-primary-500 text-white text-xs font-medium">
                          {blog.category}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-dark-800 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                      {truncateText(blog.description, 80)}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                      <span className="flex items-center gap-1">
                        <LuCalendar className="w-3 h-3" />
                        {formatDate(blog.createdAt)}
                      </span>
                      <LuExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={getPortalIcon(dashboardContent?.blogSectionIcon || 'LuNewspaper')}
              title={dashboardContent?.blogEmptyTitle || 'No blog posts available'}
              containerClassName="text-center py-8"
              iconClassName="w-12 h-12 mx-auto mb-3 opacity-50 text-gray-300 dark:text-dark-500"
              titleClassName="text-gray-500 dark:text-gray-400 text-sm font-normal"
            />
          )}

          {/* Blog Pagination */}
          {!loading && totalBlogPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8 pt-6 border-t border-gray-100 dark:border-dark-600">
              <button
                onClick={() => setBlogPage(prev => Math.max(1, prev - 1))}
                disabled={blogPage === 1}
                className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <LuChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-1 sm:gap-2">
                {(() => {
                  const pages = [];
                  const maxVisible = window.innerWidth < 640 ? 3 : 5;
                  let start = Math.max(1, blogPage - Math.floor(maxVisible / 2));
                  let end = Math.min(totalBlogPages, start + maxVisible - 1);

                  if (end - start < maxVisible - 1) {
                    start = Math.max(1, end - maxVisible + 1);
                  }

                  for (let i = start; i <= end; i++) {
                    pages.push(i);
                  }
                  return pages;
                })().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setBlogPage(pageNum)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg text-xs sm:text-sm font-medium transition-all ${blogPage === pageNum
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                      }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setBlogPage(prev => Math.min(totalBlogPages, prev + 1))}
                disabled={blogPage === totalBlogPages}
                className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <LuChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Right Column - Deadlines & Notifications */}
        <div className="space-y-6">


          {/* Notifications Card */}
          <div
            className="card p-6 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <PortalSectionIcon iconName={dashboardContent?.notificationsIcon || 'LuBell'} />
              <div>
                <h2 className="section-title">{dashboardContent?.notificationsTitle || 'Notifications'}</h2>
                {notifications.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{notifications.filter(n => !n.isRead).length} unread</p>
                )}
              </div>
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
              {notifLoading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse p-4 rounded-xl border border-gray-200 dark:border-dark-600">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-dark-600" />
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4 mb-2" />
                        <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-full mb-2" />
                        <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-1/3" />
                      </div>
                    </div>
                  </div>
                ))
              ) : notifications.length > 0 ? (
                notifications.slice(0, 10).map((notif) => {
                  const config = getNotifConfig(notif.type);
                  const IconComp = config.icon;
                  return (
                    <div
                      key={notif._id}
                      onClick={() => handleNotifClick(notif)}
                      className={`p-4 rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-md cursor-pointer ${config.borderColor} ${config.bgColor}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${config.iconBg}`}>
                          <IconComp className={`w-4 h-4 ${config.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-dark-800 dark:text-white text-sm mb-1">
                            {notif.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                            {notif.message}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {timeAgo(notif.createdAt)}
                            </span>
                            {!notif.isRead && (
                              <span className="w-2 h-2 rounded-full bg-primary-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                    {(() => {
                      const EmptyIcon = getPortalIcon(dashboardContent?.notificationsIcon || 'LuBell');
                      return <EmptyIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" />;
                    })()}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{dashboardContent?.notificationsEmptyTitle || 'No notifications yet'}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{dashboardContent?.notificationsEmptySubtitle || "You'll see updates here when there's activity"}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity / Updates Section */}
      <div
        id="whats-new-section"
        className="card p-6 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <PortalSectionIcon iconName={dashboardContent?.whatsNewIcon || 'LuRefreshCw'} />
            <div>
              <h2 className="section-title">{dashboardContent?.whatsNewTitle || "What's New"}</h2>
              {dashboardContent?.whatsNewSubtitle && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{dashboardContent.whatsNewSubtitle}</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayResources.map((resource, index) => {
            const colors = getPortalColor(resource.color);
            const Icon = getPortalIcon(resource.icon);
            const fileHref = buildResourceFileUrl(resource.fileUrl, API_BASE, bundledFallbacks[index]);
            return (
              <div key={resource.id || `resource-${index}`} className={`group relative p-6 rounded-2xl bg-white dark:bg-dark-700 border border-gray-100 dark:border-dark-600 ${colors.border} transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col h-full`}>
                <div className={`w-14 h-14 rounded-2xl ${colors.iconWrap} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-7 h-7 ${colors.icon}`} />
                </div>
                <h3 className="font-bold text-lg text-dark-800 dark:text-white mb-2 leading-tight">{resource.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 line-clamp-2">{resource.description}</p>
                {renderResourceActions(resource, colors, fileHref)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Social Media Section */}
      <div
        className="card p-6 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
      >
        <div className="flex items-center gap-3 mb-6">
          <PortalSectionIcon iconName={dashboardContent?.connectIcon || 'LuRss'} variant="secondary" />
          <div>
            <h2 className="section-title">{dashboardContent?.connectTitle || 'Connect With Us'}</h2>
            {dashboardContent?.connectSubtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{dashboardContent.connectSubtitle}</p>
            )}
          </div>
        </div>

        <div className={`grid grid-cols-1 gap-6 ${socialLinks.length > 1 ? 'md:grid-cols-2' : ''}`}>
          {socialLinks.map((link, index) => (
            <SocialLinkCard key={`social-${index}-${link.title}`} link={link} />
          ))}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
